import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { usePermissions } from '@/hooks/use-permissions';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Wastage({ user }) {
  const navigate = useNavigate();
  const { canView, canCreate, canEdit, canDelete, hasPermission } = usePermissions(user);
  
  // Check if user has permission to view wastage
  if (!canView('wastage')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to view Wastage.</p>
          <Button onClick={() => navigate('/design-studio')}>Back to Design Studio</Button>
        </div>
      </div>
    );
  }
  
  const [wastageList, setWastageList] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [selectedFabric, setSelectedFabric] = useState(null);
  const [colors, setColors] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [utilizeOpen, setUtilizeOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [deleteAuthOpen, setDeleteAuthOpen] = useState(false);
  const [selectedWastage, setSelectedWastage] = useState(null);
  const [wastageToDelete, setWastageToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form fields
  const [formData, setFormData] = useState({
    fabric_code: '',
    color_code: '',
    quantity_meters: ''
  });
  
  // Utilize form
  const [utilizeQuantity, setUtilizeQuantity] = useState('');
  const [utilizeReason, setUtilizeReason] = useState('');

  useEffect(() => {
    fetchWastage();
    fetchFabrics();
  }, []);

  const fetchWastage = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/wastage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWastageList(response.data);
    } catch (error) {
      console.error('Failed to fetch wastage:', error);
      toast.error('Failed to fetch wastage entries');
    }
  };

  // Auto-refresh every 5 seconds, but pause when dialog is open
  useAutoRefresh(fetchWastage, 5, formOpen || utilizeOpen || viewOpen || deleteAuthOpen);

  const fetchFabrics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/fabrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFabrics(response.data.map(f => ({
        value: f.ln_code,
        label: `${f.ln_code}${f.composition ? ' - ' + f.composition : ''}`
      })));
    } catch (error) {
      console.error('Failed to fetch fabrics:', error);
      toast.error('Failed to load fabrics');
    }
  };

  const handleFabricSelect = async (fabricCode) => {
    setFormData({ ...formData, fabric_code: fabricCode, color_code: '' });
    
    // Fetch fabric details to get colors
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/fabrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fabric = response.data.find(f => f.ln_code === fabricCode);
      if (fabric) {
        setSelectedFabric(fabric);
        setColors(fabric.colors.map(c => ({
          value: c.color_code,
          label: `${c.color_code} - ${c.color_name}`
        })));
      }
    } catch (error) {
      console.error('Failed to fetch fabric colors:', error);
      toast.error('Failed to load colors');
    }
  };

  const handleCreateWastage = async () => {
    if (!formData.fabric_code || !formData.color_code || !formData.quantity_meters) {
      toast.error('Please fill in all fields');
      return;
    }

    const quantity = parseFloat(formData.quantity_meters);
    if (isNaN(quantity) || quantity <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/wastage`,
        {
          fabric_code: formData.fabric_code,
          color_code: formData.color_code,
          quantity_meters: quantity
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Wastage entry created successfully');
      setFormOpen(false);
      setFormData({ fabric_code: '', color_code: '', quantity_meters: '' });
      setSelectedFabric(null);
      setColors([]);
      fetchWastage();
    } catch (error) {
      console.error('Error creating wastage:', error);
      console.error('Error response:', error.response?.data);
      
      // Handle validation errors from backend
      let errorMessage = 'Failed to create wastage entry';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors
          errorMessage = error.response.data.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof error.response.data.detail === 'object') {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleViewClick = async (wastage) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/wastage/${wastage.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedWastage(response.data);
      setViewOpen(true);
    } catch (error) {
      console.error('Error fetching wastage details:', error);
      toast.error('Failed to load wastage details');
    }
  };

  const handleUtilizeClick = (wastage) => {
    setSelectedWastage(wastage);
    setUtilizeQuantity('');
    setUtilizeReason('');
    setUtilizeOpen(true);
  };

  const handleUtilize = async () => {
    if (!utilizeQuantity) {
      toast.error('Please enter quantity to utilize');
      return;
    }

    const quantity = parseFloat(utilizeQuantity);
    if (isNaN(quantity) || quantity <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    if (quantity > selectedWastage.remaining_quantity_meters) {
      toast.error(`Cannot utilize ${quantity}m. Only ${selectedWastage.remaining_quantity_meters}m remaining`);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/wastage/${selectedWastage.id}/utilize`,
        { 
          quantity_meters: quantity,
          reason: utilizeReason || 'No reason provided'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.deleted) {
        toast.success('Wastage fully utilized and entry deleted');
      } else {
        toast.success(`Utilized ${quantity}m. ${response.data.remaining_quantity}m remaining`);
      }
      
      setUtilizeOpen(false);
      setSelectedWastage(null);
      setUtilizeQuantity('');
      setUtilizeReason('');
      fetchWastage();
    } catch (error) {
      console.error('Error utilizing wastage:', error);
      toast.error(error.response?.data?.detail || 'Failed to utilize wastage');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this wastage entry?')) {
      return;
    }
  };

  const handleDeleteClick = (wastage) => {
    setWastageToDelete(wastage);
    setDeleteAuthOpen(true);
  };

  const confirmDelete = async () => {
    if (!wastageToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/wastage/${wastageToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Wastage entry deleted successfully');
      setDeleteAuthOpen(false);
      setWastageToDelete(null);
      fetchWastage();
    } catch (error) {
      console.error('Error deleting wastage:', error);
      toast.error('Failed to delete wastage entry');
      setDeleteAuthOpen(false);
    }
  };

  const filteredWastageList = wastageList.filter(wastage => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      wastage.fabric_code.toLowerCase().includes(search) ||
      wastage.fabric_name.toLowerCase().includes(search) ||
      wastage.color_code.toLowerCase().includes(search)
    );
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/design-studio')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Wastage Management
              </h1>
              <p className="text-sm text-gray-600">Track and utilize fabric wastage</p>
            </div>
          </div>
          {canCreate('wastage') && (
            <Button onClick={() => setFormOpen(true)} className="gap-2 bg-black hover:bg-gray-800">
              <Plus className="w-4 h-4" />
              Add Wastage
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Search Bar */}
        <div className="mb-6">
          <Input
            type="text"
            placeholder="Search by fabric code, fabric name, or color code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>

        {filteredWastageList.length === 0 ? (
          <div className="bg-white border-2 border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-500">No wastage entries yet. Add your first wastage entry.</p>
          </div>
        ) : (
          <div className="bg-white border-2 border-black rounded-lg overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-50 border-b-2 border-black">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Fabric Code</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Fabric Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Color</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Original Qty</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Remaining Qty</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredWastageList.map(wastage => (
                  <tr key={wastage.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleViewClick(wastage)}
                        className="font-bold text-black hover:underline cursor-pointer"
                      >
                        {wastage.fabric_code}
                      </button>
                    </td>
                    <td className="px-4 py-3">{wastage.fabric_name}</td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium">{wastage.color_code}</span>
                    </td>
                    <td className="px-4 py-3">{wastage.original_quantity_meters}m</td>
                    <td className="px-4 py-3">
                      <span className="font-bold text-black">{wastage.remaining_quantity_meters}m</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {hasPermission('wastage_utilize') && (
                          <Button
                            onClick={() => handleUtilizeClick(wastage)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            Utilize
                          </Button>
                        )}
                        {canDelete('wastage') && (
                          <Button
                            onClick={() => handleDeleteClick(wastage)}
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-600 hover:bg-red-50"
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Create Wastage Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-tight">Add Wastage Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Fabric</Label>
              <SearchableSelect
                options={fabrics}
                value={formData.fabric_code}
                onChange={handleFabricSelect}
                placeholder="Select fabric..."
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Color</Label>
              <SearchableSelect
                options={colors}
                value={formData.color_code}
                onChange={(value) => setFormData({ ...formData, color_code: value })}
                placeholder="Select color..."
                className="mt-1"
                disabled={!formData.fabric_code}
              />
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Quantity (meters)</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={formData.quantity_meters}
                onChange={(e) => setFormData({ ...formData, quantity_meters: e.target.value })}
                placeholder="Enter quantity in meters"
                className="mt-1"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={() => setFormOpen(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleCreateWastage} className="flex-1 bg-black hover:bg-gray-800">
                Save Wastage
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Utilize Wastage Dialog */}
      <Dialog open={utilizeOpen} onOpenChange={setUtilizeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-tight">Utilize Wastage</DialogTitle>
          </DialogHeader>
          {selectedWastage && (
            <div className="space-y-4">
              <div className="p-4 border-2 border-blue-500 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-800">
                  <strong>Fabric:</strong> {selectedWastage.fabric_code} - {selectedWastage.fabric_name}
                </p>
                <p className="text-sm font-medium text-blue-800 mt-1">
                  <strong>Color:</strong> {selectedWastage.color_code} - {selectedWastage.color_name}
                </p>
                <p className="text-sm font-medium text-blue-800 mt-1">
                  <strong>Available:</strong> {selectedWastage.remaining_quantity_meters}m
                </p>
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">
                  Quantity to Utilize (meters)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={selectedWastage.remaining_quantity_meters}
                  value={utilizeQuantity}
                  onChange={(e) => setUtilizeQuantity(e.target.value)}
                  placeholder="Enter quantity"
                  className="mt-1"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum: {selectedWastage.remaining_quantity_meters}m
                </p>
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">
                  Reason for Utilization
                </Label>
                <Input
                  type="text"
                  value={utilizeReason}
                  onChange={(e) => setUtilizeReason(e.target.value)}
                  placeholder="Enter reason (optional)"
                  className="mt-1"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button onClick={() => setUtilizeOpen(false)} variant="outline" className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleUtilize} className="flex-1 bg-green-600 hover:bg-green-700 text-white">
                  Utilize
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Wastage Details Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-tight">Wastage Details</DialogTitle>
          </DialogHeader>
          {selectedWastage && (
            <div className="space-y-6">
              {/* Wastage Summary */}
              <div className="p-4 border-2 border-black rounded-lg bg-gray-50">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase">Fabric Code</p>
                    <p className="text-lg font-bold">{selectedWastage.fabric_code}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase">Fabric Name</p>
                    <p className="text-lg font-bold">{selectedWastage.fabric_name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase">Color</p>
                    <p className="text-lg font-bold">{selectedWastage.color_code} - {selectedWastage.color_name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase">Remaining Quantity</p>
                    <p className="text-2xl font-bold text-blue-600">{selectedWastage.remaining_quantity_meters}m</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-300">
                  <div className="flex justify-between">
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase">Original Quantity</p>
                      <p className="text-lg font-bold">{selectedWastage.original_quantity_meters}m</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase">Total Utilized</p>
                      <p className="text-lg font-bold text-green-600">
                        {(selectedWastage.original_quantity_meters - selectedWastage.remaining_quantity_meters).toFixed(2)}m
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Utilization History - Ledger Style */}
              <div>
                <h3 className="text-lg font-bold uppercase tracking-tight mb-3">Utilization History</h3>
                {selectedWastage.utilization_history && selectedWastage.utilization_history.length > 0 ? (
                  <div className="border-2 border-black rounded-lg overflow-hidden">
                    <table className="min-w-full">
                      <thead className="bg-gray-50 border-b-2 border-black">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Quantity</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Reason</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">By</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedWastage.utilization_history.map((record, index) => {
                          const isReturn = record.quantity_meters < 0;
                          const displayQuantity = Math.abs(record.quantity_meters);
                          return (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm">
                                {new Date(record.utilized_at).toLocaleDateString()} {new Date(record.utilized_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </td>
                              <td className="px-4 py-3">
                                {isReturn ? (
                                  <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800">INWARD</span>
                                ) : (
                                  <span className="px-2 py-1 text-xs font-semibold rounded bg-orange-100 text-orange-800">OUTWARD</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`font-bold ${isReturn ? 'text-blue-600' : 'text-green-600'}`}>
                                  {isReturn ? '+' : '-'}{displayQuantity}m
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm">{record.reason || 'No reason provided'}</td>
                              <td className="px-4 py-3 text-sm font-medium">{record.utilized_by || 'Unknown'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-8 border-2 border-gray-200 rounded-lg text-center">
                    <p className="text-gray-500">No utilization history yet</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <Button onClick={() => setViewOpen(false)} className="bg-black hover:bg-gray-800">
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Authorization Dialog */}
      <AuthorizationDialog
        open={deleteAuthOpen}
        onOpenChange={setDeleteAuthOpen}
        onAuthorized={confirmDelete}
        title="Delete Wastage Entry"
        message={wastageToDelete ? `Enter authorization code to delete wastage entry for ${wastageToDelete.fabric_code} - ${wastageToDelete.color_code}` : 'Enter authorization code to proceed'}
      />

    </div>
  );
}
